import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { FilterableDataTable, DrawerPanel, ConfirmDialog, ImageUpload } from '../../components/shared';
import type { RowAction, DrawerAction } from '../../components/shared';
import { MachinePartTypeControllerApi } from '../../api-generated/apis';
import type { MachinePartType, MeasurementSpec } from '../../api-generated/models';
import { apiConfig } from '../../api-generated/apiConfig';

const partTypeApi = new MachinePartTypeControllerApi(apiConfig);

interface PartTypeRow {
  id: string;
  name: string;
  partNumber: string;
  specCount: number;
}

const columns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
  { field: 'partNumber', headerName: 'Part Number', flex: 0.8, minWidth: 120 },
  { field: 'specCount', headerName: 'Measurements', flex: 0.5, minWidth: 110 },
];

const PartTypesPanel: React.FC = () => {
  const queryClient = useQueryClient();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formPartNumber, setFormPartNumber] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formImageUrl, setFormImageUrl] = useState('');
  const [specs, setSpecs] = useState<MeasurementSpec[]>([]);
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: partTypes, isLoading } = useQuery<MachinePartType[]>({
    queryKey: ['machine-part-types'],
    queryFn: () => partTypeApi.getAllMachinePartTypes(),
  });

  const rows: PartTypeRow[] = useMemo(
    () =>
      (partTypes ?? []).map((pt: MachinePartType) => ({
        id: pt.id ?? '',
        name: pt.name ?? '',
        partNumber: pt.partNumber ?? '',
        specCount: pt.measurementSpecs?.length ?? 0,
      })),
    [partTypes],
  );

  const saveMutation = useMutation({
    mutationFn: (data: { id?: string; body: MachinePartType }) => {
      if (data.id) {
        return partTypeApi.updateMachinePartType({ id: data.id, machinePartType: data.body });
      }
      return partTypeApi.createMachinePartType({ machinePartType: data.body });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machine-part-types'] });
      setDrawerOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => partTypeApi.deleteMachinePartType({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machine-part-types'] });
      setConfirmOpen(false);
    },
  });

  const resetForm = () => {
    setFormName('');
    setFormPartNumber('');
    setFormDescription('');
    setFormImageUrl('');
    setSpecs([]);
    setErrors({});
  };

  const handleAdd = useCallback(() => {
    setEditingId(null);
    resetForm();
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback(
    (row: PartTypeRow) => {
      const pt = (partTypes ?? []).find((p: MachinePartType) => p.id === row.id);
      setEditingId(row.id);
      setFormName(pt?.name ?? '');
      setFormPartNumber(pt?.partNumber ?? '');
      setFormDescription(pt?.description ?? '');
      setFormImageUrl(pt?.imageUrl ?? '');
      setSpecs(pt?.measurementSpecs ? [...pt.measurementSpecs] : []);
      setErrors({});
      setDrawerOpen(true);
    },
    [partTypes],
  );

  const updateSpec = (idx: number, field: keyof MeasurementSpec, value: string) => {
    setSpecs((prev) =>
      prev.map((s, i) =>
        i === idx
          ? {
              ...s,
              [field]:
                field === 'wearLimit' || field === 'baselineValue'
                  ? value === ''
                    ? undefined
                    : Number(value)
                  : value,
            }
          : s,
      ),
    );
  };

  const addSpec = () => setSpecs((prev) => [...prev, { key: '', label: '', unit: '' }]);
  const removeSpec = (idx: number) => setSpecs((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = useCallback(() => {
    if (!formName.trim()) {
      setErrors({ name: 'Name is required' });
      return;
    }
    const body: MachinePartType = {
      name: formName.trim(),
      partNumber: formPartNumber.trim() || undefined,
      description: formDescription.trim() || undefined,
      imageUrl: formImageUrl.trim() || undefined,
      measurementSpecs: specs.filter((s) => s.key && s.key.trim()),
    };
    saveMutation.mutate({ id: editingId ?? undefined, body });
  }, [formName, formPartNumber, formDescription, formImageUrl, specs, editingId, saveMutation]);

  const rowActions: RowAction<PartTypeRow>[] = [
    { label: 'Edit', icon: EditIcon, onClick: handleEdit },
    { label: 'Delete', icon: DeleteIcon, onClick: (row) => { setDeleteId(row.id); setConfirmOpen(true); } },
  ];

  const drawerActions: DrawerAction[] = [
    { label: 'Cancel', variant: 'outlined', onClick: () => setDrawerOpen(false) },
    { label: editingId ? 'Update' : 'Create', variant: 'contained', onClick: handleSave, disabled: !formName },
  ];

  return (
    <>
      <FilterableDataTable<PartTypeRow>
        rows={rows}
        columns={columns}
        loading={isLoading}
        totalCount={rows.length}
        page={0}
        pageSize={25}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        actions={rowActions}
        primaryAction={{ label: 'Add Part Type', onClick: handleAdd }}
        searchPlaceholder="Search part types..."
      />
      <DrawerPanel
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editingId ? 'Edit Part Type' : 'New Part Type'}
        width="medium"
        actions={drawerActions}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            value={formName}
            onChange={(e) => { setFormName(e.target.value); setErrors({}); }}
            required
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            size="small"
          />
          <TextField label="Part Number" value={formPartNumber} onChange={(e) => setFormPartNumber(e.target.value)} fullWidth size="small" />
          <TextField label="Description" value={formDescription} onChange={(e) => setFormDescription(e.target.value)} fullWidth size="small" multiline rows={2} />
          <ImageUpload
            label="Part Type Image"
            value={formImageUrl}
            onChange={setFormImageUrl}
            purpose="part-type"
            helperText="Upload a reference image for this part kind. Stored in S3; saved with the part type."
          />

          <Divider />
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">Measurement Specs</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addSpec}>Add</Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Condemn limit + as-new baseline per measured dimension. Defined once for this kind.
          </Typography>

          {specs.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
              No measurement specs.
            </Typography>
          )}

          {specs.map((spec, idx) => (
            <Box key={idx} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">Spec #{idx + 1}</Typography>
                <IconButton size="small" color="error" onClick={() => removeSpec(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <TextField label="Key" value={spec.key ?? ''} onChange={(e) => updateSpec(idx, 'key', e.target.value)} size="small" required helperText="e.g. treadElongation" />
                <TextField label="Label" value={spec.label ?? ''} onChange={(e) => updateSpec(idx, 'label', e.target.value)} size="small" />
                <TextField label="Unit" value={spec.unit ?? ''} onChange={(e) => updateSpec(idx, 'unit', e.target.value)} size="small" placeholder="mm" />
                <Box />
                <TextField label="Wear Limit" type="number" value={spec.wearLimit ?? ''} onChange={(e) => updateSpec(idx, 'wearLimit', e.target.value)} size="small" />
                <TextField label="Baseline (as-new)" type="number" value={spec.baselineValue ?? ''} onChange={(e) => updateSpec(idx, 'baselineValue', e.target.value)} size="small" />
              </Box>
            </Box>
          ))}
        </Box>
      </DrawerPanel>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => deleteId != null && deleteMutation.mutate(deleteId)}
        title="Delete Part Type"
        message="Are you sure you want to delete this part type? This action cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
      />
    </>
  );
};

export default PartTypesPanel;
